/**
 * Centralized fetch wrapper. Routes requests to one of two services based on
 * the URL prefix:
 *
 *   /api/*      → Control API (Node Fastify, orgs/envs/scans/watches → kcp)
 *   /data-api/* → Data API (Go Echo, long-term storage: findings/audit/reports)
 *
 * Both accept the same Keycloak-issued JWT. In dev with MSW, the worker intercepts
 * both prefixes before they leave the origin — no real network call.
 *
 * Responsibilities:
 *   - Pick the right base URL by prefix.
 *   - Attach the Bearer JWT.
 *   - Surface a consistent ApiError shape.
 *   - Stay out of the way — no global state, no React imports.
 */

export type ApiError = {
  code: string
  message: string
  detail?: string
  hint?: string
  /** HTTP status code — added by the wrapper, not the server. */
  status?: number
}

/**
 * True when an `ApiError` means "this backend doesn't implement the feature
 * yet" rather than a real failure: a control-API honest 501, the data-plane
 * "not connected" placeholder (`data_api_unreachable`), or an explicit
 * `not_implemented` code.
 *
 * Used ONLY to skip pointless retries (retrying a 501 can't succeed). We do
 * NOT hide widgets on this — the error still surfaces so an unwired/broken
 * backend is always visible with an error+retry state.
 */
export function isAbsentFeature(error: unknown): boolean {
  const e = error as Partial<ApiError> | null | undefined
  return e?.status === 501 || e?.code === 'not_implemented' || e?.code === 'data_api_unreachable'
}

function getToken(): string | null {
  const raw = sessionStorage.getItem('it.session')
  if (!raw) return null
  try {
    return (JSON.parse(raw) as { token?: string }).token ?? null
  } catch {
    return null
  }
}

/**
 * MSW service workers are origin-scoped — requests with absolute URLs to a
 * different origin escape MSW and hit the (non-listening) backend port. So
 * when mocks are on, always return relative same-origin paths.
 */
function baseUrlFor(path: string): string {
  if (import.meta.env.VITE_USE_MOCKS !== 'false') return ''

  if (path.startsWith('/data-api/')) {
    // Always same-origin: dev = Vite proxy (target VITE_DATA_API_URL),
    // container = nginx. Keeps CORS out of the picture entirely.
    return ''
  }
  // Default: control API.
  return import.meta.env.VITE_API_URL ?? ''
}

/**
 * Endpoints owned by the Data API (backend/dataapi/api/openapi.yaml flat
 * queries). With mocks off these are rewritten onto the `/data-api` prefix so
 * they reach the real service (remote via the dev proxy, Kong in containers)
 * instead of the control API. Everything NOT in the Data API spec (facets,
 * audit views, compliance, …) stays on the control API's honest 501.
 */
// `/api/v1/identities` moved OFF this list: the control API now serves the
// directory straight from the per-env kcp resource workspaces.
const DATA_API_EXACT = [
  '/api/v1/findings',
  '/api/v1/audit',
  '/api/v1/reports',
  '/api/v1/activity',
  '/api/v1/posture/summary',
  '/api/v1/analyses',
  '/api/v1/notifications',
]
const DATA_API_PREFIXES = [
  '/api/v1/findings/',
  '/api/v1/captures/',
  '/api/v1/reports/',
  '/api/v1/activity/',
  '/api/v1/audit/',
  '/api/v1/posture/',
  '/api/v1/analyses/',
  '/api/v1/notifications/',
  '/api/v1/drive/',
]
// Per-env Data API aliases, matched exactly — a bare `/api/v1/envs/` prefix
// would swallow the control API's other env routes (captures, analyze, …).
const DATA_API_REGEX = [/^\/api\/v1\/envs\/[^/]+\/analyses$/]

function routeDataPlane(path: string): string {
  if (import.meta.env.VITE_USE_MOCKS !== 'false') return path
  if (
    DATA_API_EXACT.includes(path) ||
    DATA_API_PREFIXES.some((p) => path.startsWith(p)) ||
    DATA_API_REGEX.some((r) => r.test(path))
  ) {
    return `/data-api${path}`
  }
  return path
}

/** Mocks are on unless explicitly disabled (prod flavor sets 'false'). */
const MOCKS_ON = import.meta.env.VITE_USE_MOCKS !== 'false'

/**
 * Silent re-auth on 401.
 *
 * The BFF keeps only the access token in its sealed session cookie (no refresh
 * token — it would push the cookie past the ~4KB limit). Once that token's `exp`
 * passes, every control-API route answers 401 while the SPA still shows a
 * logged-in shell from the cached session. Recover by bouncing through the BFF
 * login: Keycloak's still-valid SSO session re-issues with no form shown and
 * sets a fresh cookie — the same trick the post-org-create flow already uses.
 *
 * Guard: redirect at most once per window. If the fresh token *also* 401s (clock
 * skew, or a resource that answers 401 instead of 403), surface the error rather
 * than looping login → app → 401 → login.
 */
const REAUTH_WINDOW_MS = 30_000
function bffUrl(): string {
  return import.meta.env.VITE_BFF_URL ?? import.meta.env.VITE_API_URL ?? ''
}
function trySilentReauth(): void {
  if (MOCKS_ON) return
  const last = Number(sessionStorage.getItem('it.reauthAt') ?? 0)
  if (Date.now() - last < REAUTH_WINDOW_MS) return
  sessionStorage.setItem('it.reauthAt', String(Date.now()))
  window.location.href = `${bffUrl()}/api/v1/auth/login`
}

/**
 * Re-register this tab with the MSW service worker.
 *
 * When the browser hard-kills the idle worker (system sleep, memory pressure,
 * Chrome's SW lifetime cap) it restarts on the next fetch with an empty client
 * set — the KEEPALIVE heartbeat keeps it warm but never re-adds this client, so
 * requests fall through (`onUnhandledRequest: 'bypass'`) to the Vite dev server,
 * which answers a bare 404 "Not Found". Only `worker.start()` (via MOCK_ACTIVATE)
 * re-registers the client. start() is idempotent, so calling it here is safe.
 * The dynamic import keeps the mock chunk out of the prod (`VITE_USE_MOCKS=false`)
 * bundle — this only runs when MOCKS_ON.
 */
async function reactivateMocks(): Promise<void> {
  try {
    const { worker } = await import('@/mocks/browser')
    await worker.start({
      onUnhandledRequest: 'bypass',
      serviceWorker: { url: '/mockServiceWorker.js' },
      quiet: true,
    })
  } catch {
    /* not in a mock-capable context — nothing to reactivate */
  }
}

async function readError(resp: Response): Promise<ApiError> {
  let body: ApiError
  try {
    body = (await resp.json()) as ApiError
  } catch {
    body = { code: 'http_error', message: resp.statusText }
  }
  body.status = resp.status
  return body
}

export type ApiRequest = {
  url: string
  method?: string
  params?: Record<string, string | number | boolean | undefined | null>
  data?: unknown
  headers?: Record<string, string>
  signal?: AbortSignal
  /** When true, response is returned as raw Response (e.g. for SSE). */
  raw?: boolean
}

export async function apiFetch<T>(req: ApiRequest): Promise<T> {
  const { method = 'GET', params, data, headers, signal, raw } = req
  const url = routeDataPlane(req.url)

  const qs = params
    ? '?' +
      Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join('&')
    : ''

  const fullUrl = `${baseUrlFor(url)}${url}${qs}`

  const token = getToken()
  // The BFF scopes Control API calls to a kcp workspace. The selected org id
  // (org switcher, `it.orgId`) names the tenant workspace; the BFF resolves
  // it under its configured tenants parent.
  const orgId = sessionStorage.getItem('it.orgId')
  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...(data ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(orgId && url.startsWith('/api/') ? { 'X-Workspace': orgId } : {}),
    // The Data API validates the selected org against the token's
    // memberships (multi-org users) — same value, its own header.
    ...(orgId && url.startsWith('/data-api/') ? { 'X-Org': orgId } : {}),
    ...headers,
  }

  const send = () =>
    fetch(fullUrl, {
      method,
      headers: finalHeaders,
      body: data ? JSON.stringify(data) : undefined,
      signal,
      credentials: 'include',
    })

  let resp = await send()

  if (raw) return resp as unknown as T

  if (!resp.ok) {
    let body = await readError(resp)
    // A 404 that isn't a real MSW `not_found` envelope, while mocks are on, means
    // the request bypassed a restarted worker and hit Vite. Re-register and retry
    // once — this is what makes a stale tab (and the Retry button) self-heal
    // instead of showing "Not Found" until a full reload. See reactivateMocks.
    if (MOCKS_ON && resp.status === 404 && body.code !== 'not_found') {
      await reactivateMocks()
      resp = await send()
      if (!resp.ok) throw await readError(resp)
    } else {
      // Expired BFF session cookie → bounce through Keycloak for a fresh one.
      if (resp.status === 401) trySilentReauth()
      throw body
    }
  }

  if (resp.status === 204) return undefined as T

  // The remote edge currently answers /data-api/* with the SPA placeholder
  // (the route to the Go service isn't deployed yet). A 200 that isn't JSON
  // means "nothing is listening at this path" — surface that honestly
  // instead of a JSON parse crash.
  if (!(resp.headers.get('content-type') ?? '').includes('json')) {
    throw {
      code: 'data_api_unreachable',
      message: 'Not connected — the Data API is not reachable in this deployment yet.',
      status: 503,
    } satisfies ApiError
  }
  return (await resp.json()) as T
}
